/**
 * Sidebar — Translation editing panel
 * Shows original text, translation textarea, live preview, and page blocks list.
 */

import type { ContentUnit } from '../../content/types.js';
import type { ResolvedKey } from './key-resolver.js';
import { HTMLSerializer } from '../../content/serializer/HTMLSerializer.js';
import { escapeHtml } from '../../content/sanitizer/HTMLSanitizer.js';
import { applyLivePreview } from './live-preview.js';
import type { EditorAPI, TargetStatus, EditorWorkflowInfo } from './editor-api.js';
import { isRowAtPostreview } from './editor-api.js';
import { BlockExtractor } from '../block-extractor.js';
import { generateAutoKey } from '../md5.js';
import { localizeUrl } from './locale-url.js';

const htmlSerializer = new HTMLSerializer();

export class Sidebar {
  private el: HTMLDivElement;
  private bodyEl: HTMLDivElement;
  private statusEl: HTMLDivElement;
  private api: EditorAPI;
  private locale: string;
  private namespace: string;
  private defaultLocale: string;
  private current: ResolvedKey | null = null;
  private blockExtractor: BlockExtractor;
  private onBlockClick: (resolved: ResolvedKey) => void;
  private statusMap: Map<string, TargetStatus | null> = new Map();
  private unsavedKeys: Set<string> = new Set();
  private onProgressUpdate: ((translated: number, total: number) => void) | null = null;
  private lastFetchTime = 0;
  private lastFlushTime = 0;

  private userRole: string;
  // Latest known workflow context for this.locale. Revalidated on every strip
  // render through the API's TTL-bound, per-locale cache — the workflow can
  // change mid-session (dashboard settings) and can differ per locale
  // (per-target override), so it must not be pinned at first fetch.
  private workflowInfo: EditorWorkflowInfo | null = null;

  constructor(
    shadowRoot: ShadowRoot,
    api: EditorAPI,
    locale: string,
    namespace: string,
    defaultLocale: string,
    onBlockClick: (resolved: ResolvedKey) => void,
    userRole: string = '',
  ) {
    this.api = api;
    this.locale = locale;
    this.namespace = namespace;
    this.defaultLocale = defaultLocale;
    this.onBlockClick = onBlockClick;
    this.userRole = userRole;
    this.blockExtractor = new BlockExtractor();

    this.el = document.createElement('div');
    this.el.className = 'lr-sidebar';

    // Resize handle
    const resizeHandle = document.createElement('div');
    resizeHandle.className = 'lr-sidebar-resize';
    this.initResize(resizeHandle);
    this.el.appendChild(resizeHandle);

    // Header
    const header = document.createElement('div');
    header.className = 'lr-sidebar-header';
    const title = document.createElement('span');
    title.className = 'lr-sidebar-title';
    title.textContent = 'Edit Translation';
    header.appendChild(title);
    const closeBtn = document.createElement('button');
    closeBtn.className = 'lr-sidebar-close';
    closeBtn.innerHTML = '&times;';
    closeBtn.addEventListener('click', () => this.close());
    header.appendChild(closeBtn);
    this.el.appendChild(header);

    // Body
    this.bodyEl = document.createElement('div');
    this.bodyEl.className = 'lr-sidebar-body';
    this.el.appendChild(this.bodyEl);

    // Status bar (rendered inline in renderContent, above preview)
    this.statusEl = document.createElement('div');
    this.statusEl.className = 'lr-status';

    // Wire up API status
    this.api.setStatusCallback((status, message) => {
      this.statusEl.textContent = message || status;
      this.statusEl.className = `lr-status ${status}`;
    });

    // After flush, re-fetch status for saved keys and refresh blocks list
    this.api.setFlushedCallback(async (keys) => {
      this.lastFlushTime = Date.now();
      const freshStatuses = await this.api.fetchStatus(keys, this.locale, this.namespace);
      for (const [key, status] of Object.entries(freshStatuses)) {
        this.statusMap.set(key, status);
        this.unsavedKeys.delete(key);
      }
      this.refreshBlocksList();
      this.emitProgress();
    });

    // Console diagnostic: dump every block's key + full ContentUnit text +
    // styles so the operator can compare against what's stored on the
    // server. Useful when the same source sentence appears with multiple
    // auto_keys (PHP↔JS parity divergence) — paste the output here and
    // we'll spot the difference.
    //
    // Usage from DevTools:  __lr_dump_keys()
    if (typeof window !== 'undefined') {
      (window as unknown as Record<string, unknown>).__lr_dump_keys = () => {
        const blocks = this.blockExtractor.extract(document.body, true);
        const rows = blocks.map((b, i) => {
          const s = this.statusMap.get(b.key);
          return {
            i,
            key: b.key,
            text: b.unit.text,
            stylesJson: JSON.stringify(b.unit.styles),
            statusText: s?.text ?? '',
            isReady: !!s?.isReady,
            hasTranslation: !!s?.text,
          };
        });
        console.table(rows);
        return rows;
      };

      // Diagnostic/e2e hooks that drive the SAME save path the sidebar
      // textarea uses (queueSave → batched flush → POST /targets/batch →
      // flushed-callback status re-fetch). The editor UI lives in a closed
      // shadow root — intentionally unreachable from page JS and from
      // Playwright — so automated tests exercise the save round-trip
      // through these instead of synthetic textarea events.
      //
      //   __lr_editor_edit('auto_xxx', 'nuevo texto')  — queue an edit
      //   await __lr_editor_flush()                    — force the batch POST now
      (window as unknown as Record<string, unknown>).__lr_editor_edit = (key: string, text: string) => {
        const blocks = this.blockExtractor.extract(document.body, true);
        const block = blocks.find((b) => b.key === key);
        if (!block) throw new Error(`[__lr_editor_edit] no block on page with key ${key}`);
        this.unsavedKeys.add(key);
        this.api.queueSave({
          key,
          locale: this.locale,
          namespace: this.namespace,
          text,
          styles: block.unit.styles,
        });
      };
      (window as unknown as Record<string, unknown>).__lr_editor_flush = () => this.api.flush();
    }

    shadowRoot.appendChild(this.el);
  }

  open(resolved: ResolvedKey): void {
    this.current = resolved;
    this.el.classList.add('open');
    this.renderContent(resolved);
  }

  close(): void {
    this.current = null;
    this.el.classList.remove('open');
  }

  isOpen(): boolean {
    return this.el.classList.contains('open');
  }

  updateLocale(locale: string): void {
    this.locale = locale;
    // Clear status on locale change — will be re-fetched
    this.statusMap.clear();
    this.unsavedKeys.clear();
  }

  setProgressCallback(cb: (translated: number, total: number) => void): void {
    this.onProgressUpdate = cb;
  }

  /**
   * Hydrate the status map with server data for all page blocks.
   * Called once on editor init.
   */
  async hydrateStatus(): Promise<void> {
    const blocks = this.blockExtractor.extract(document.body, true);
    const keys = blocks.map((b) => b.key);
    if (keys.length === 0) return;

    const statuses = await this.api.fetchStatus(keys, this.locale, this.namespace);
    for (const [key, status] of Object.entries(statuses)) {
      this.statusMap.set(key, status);
    }

    // Apply draft translations to page blocks that the server didn't render
    // (server only ships is_ready_for_delivery; the editor shows drafts too).
    //
    // Blocks without a server translation are the NORMAL state of any page
    // mid-translation (theme chrome, nav, fresh content) — not warning-
    // worthy by themselves. But this is also the one spot where a real
    // JS↔server key-parity bug would surface, and for untranslated
    // STAMPED blocks we can check it definitively: the element's current
    // markup IS the source (nothing was replaced), so recomputing the
    // auto_ key client-side must reproduce the server's stamp exactly.
    // A mismatch means the two sides hash the same source differently —
    // entity decoding, marker syntax, trim rules — and every lookup for
    // that block will silently miss. Shout about those; only debug-log
    // the plain untranslated count.
    const divergent: Array<{ stamped: string; recomputed: string; text: string }> = [];
    let untranslatedCount = 0;
    for (const block of blocks) {
      const status = this.statusMap.get(block.key);
      if (status && status.text && !status.isReady) {
        applyLivePreview(block.element, status.text, block.unit.styles, block.key, this.locale, this.namespace, block.unit.text, status.rowState);
      } else if (!status || !status.text) {
        untranslatedCount++;
        const stamp = block.element.getAttribute('data-lr-block');
        if (stamp && stamp.startsWith('auto_')) {
          const recomputed = generateAutoKey(block.unit.text);
          if (recomputed !== stamp) {
            divergent.push({ stamped: stamp, recomputed, text: block.unit.text.slice(0, 80) });
          }
        }
      }
    }
    if (divergent.length > 0) {
      console.warn(
        `[LionRapid Editor] ${divergent.length} block(s) where the client-side auto_ key does NOT ` +
        'match the server\'s data-lr-block stamp — JS and server key generation are diverging ' +
        'for these inputs. Sample (first 5):',
        divergent.slice(0, 5),
      );
    } else if (untranslatedCount > 0) {
      console.debug(
        `[LionRapid Editor] ${untranslatedCount} block(s) untranslated (no server text yet) — normal for in-progress pages.`,
      );
    }

    this.emitProgress();
  }

  getTranslatedCount(): number {
    let count = 0;
    for (const status of this.statusMap.values()) {
      if (status && status.rowState !== 'empty') count++;
    }
    return count;
  }

  getTotalCount(): number {
    return this.blockExtractor.extract(document.body, true).length;
  }

  private emitProgress(): void {
    if (this.onProgressUpdate) {
      this.onProgressUpdate(this.getTranslatedCount(), this.getTotalCount());
    }
  }

  destroy(): void {
    this.el.remove();
  }

  private async renderContent(resolved: ResolvedKey): Promise<void> {
    const { key, unit, element } = resolved;
    this.bodyEl.innerHTML = '';

    // Smart fetch: if no flush since last full fetch, only fetch the clicked key.
    // Otherwise re-fetch all blocks to pick up server-side changes from our saves.
    const isStale = this.lastFlushTime > this.lastFetchTime || this.lastFetchTime === 0;

    if (isStale) {
      const blocks = this.blockExtractor.extract(document.body, true);
      const allKeys = blocks.map((b) => b.key);
      if (!allKeys.includes(key)) allKeys.push(key);
      const freshStatuses = await this.api.fetchStatus(allKeys, this.locale, this.namespace);
      for (const [k, s] of Object.entries(freshStatuses)) {
        this.statusMap.set(k, s);
      }
      this.lastFetchTime = Date.now();
    } else {
      // Fresh — only fetch the clicked key
      const freshStatus = await this.api.fetchStatus([key], this.locale, this.namespace);
      if (freshStatus[key] !== undefined) {
        this.statusMap.set(key, freshStatus[key]);
      }
    }
    const status = this.statusMap.get(key) ?? null;

    // Key field
    this.addField('Key', () => {
      const keyEl = document.createElement('div');
      keyEl.className = 'lr-field-key';
      keyEl.textContent = key;
      return keyEl;
    });

    // Original text
    this.addField('Original', () => {
      const origEl = document.createElement('div');
      origEl.className = 'lr-field-original';
      origEl.innerHTML = this.renderTextWithTags(unit.text);
      return origEl;
    });

    // If this block is (or contains) a same-origin link — e.g. a nav menu
    // item — offer an explicit way to follow it. Clicking page links is
    // intercepted by the inspector (so the drawer can open), so without this
    // the PM has no way to navigate to that page to translate it. Navigation
    // is deliberate (no accidental loss of edits) and the session rides along
    // via the sessionStorage JWT + __lr_editor_active cookie.
    this.renderLinkNav(element);

    const isSourceLocale = this.locale === this.defaultLocale;
    const isLocked = isSourceLocale || status?.isLocked || status?.isReady || false;
    // Dashboard parity: rows the caller can't act on render read-only, same as
    // the dashboard row list. Existing row → server-computed canEdit
    // (can_user_edit_row: stage assignment, postreview, PM override). Missing
    // row → can_create from targets/status (creation is stage-0 work; the
    // batch save refuses it for e.g. a stage-1 proofreader, so don't offer
    // an edit that can't be saved).
    const cannotEdit = status ? status.canEdit === false : !this.api.canCreateMissing;
    const readOnly = isLocked || cannotEdit;

    // Translation textarea
    const textarea = document.createElement('textarea');
    this.addField('Translation', () => {
      textarea.className = 'lr-textarea';
      // Pre-fill: server status > in-DOM translation > empty
      textarea.value = status?.text || resolved.translatedUnit?.text || '';
      textarea.placeholder = unit.text; // Show source as placeholder
      if (readOnly) {
        textarea.readOnly = true;
        textarea.classList.add('lr-textarea-locked');
        textarea.title = isSourceLocale
          ? 'Source language — switch to a target language to edit'
          : status?.isReady ? 'Approved for delivery — read only'
          : isLocked ? 'Locked — read only'
          : status ? 'Assigned to another stage — read only'
          : 'Not yet translated — rows are created at the translation stage';
      }
      return textarea;
    });

    // Status bar (above preview)
    this.bodyEl.appendChild(this.statusEl);

    // Preview
    const previewEl = document.createElement('div');
    this.addField('Preview', () => {
      previewEl.className = 'lr-preview';
      if (textarea.value) {
        previewEl.innerHTML = this.renderPreview(textarea.value, unit.styles);
      }
      return previewEl;
    });

    // Workflow actions (Phase 3) — only on target locales (not source).
    // Visibility per role + state is enforced server-side; we hide buttons
    // the server would refuse so users don't see dead ends.
    if (!isSourceLocale) {
      this.renderWorkflowActions(key, status);
    }

    // Live preview on input (only if editable)
    if (!readOnly) {
      textarea.addEventListener('input', () => {
        const text = textarea.value;
        previewEl.innerHTML = this.renderPreview(text, unit.styles);
        if (text) {
          // When the operator types in the textarea the row becomes 'edited'
          // — pass that so applyLivePreview respects the typed text exactly
          // (no auto-wrap rescue: if they typed plain, they meant plain).
          applyLivePreview(element, text, unit.styles, key, this.locale, this.namespace, unit.text, 'edited');
        }
        this.unsavedKeys.add(key);
        this.api.queueSave({
          key,
          locale: this.locale,
          namespace: this.namespace,
          text,
          styles: unit.styles,
        });
      });
    }

    // Page blocks list
    this.renderBlocksList();

    // Focus textarea (only if editable)
    if (!readOnly) {
      setTimeout(() => textarea.focus(), 100);
    }
  }

  /**
   * Render Confirm/Reject/Mark-Ready/Unmark-Ready buttons under the preview.
   * Visibility:
   *   - Confirm: shown when row is not locked and not already ready
   *   - Reject:  shown when row is not locked and not already ready (server
   *              returns 400 if stage_index is already 0)
   *   - Unconfirm: shown when isConfirmed
   *   - Mark Ready: PM/admin only, hidden when already ready
   *   - Unmark Ready: PM/admin only, only when ready
   * After any successful click we re-fetch status for this key so the UI
   * reflects the new state and the unaffected buttons hide/show themselves.
   */
  private renderWorkflowActions(key: string, status: TargetStatus | null | undefined): void {
    const isPM = this.userRole === 'project_manager' || this.userRole === 'admin';
    const isReady = !!status?.isReady;
    const isLocked = !!status?.isLocked;
    const isConfirmed = !!status?.isConfirmed;
    // Server-computed canEdit reflects can_user_edit_row() — false for
    // rows past the user's stage assignment, postreview rows, etc.
    // Default to true if absent (older backend or unknown — let the user
    // try; server enforces 403 anyway). Missing rows fall back to the
    // session's create permission (targets/status can_create).
    const canEdit = status ? status.canEdit !== false : this.api.canCreateMissing;

    const composite = `${this.locale}:${this.namespace}:${key}`;

    const wrap = document.createElement('div');
    wrap.className = 'lr-workflow-actions';

    // Workflow context strip — renders synchronously from cache when present,
    // otherwise inserts a placeholder and back-fills after lazy-fetch resolves.
    // The strip lives at the top of the actions wrap so the user sees 'where
    // am I' before the action buttons / out-of-scope note.
    const stripHost = document.createElement('div');
    stripHost.className = 'lr-wf-strip-host';
    wrap.appendChild(stripHost);
    this.renderWorkflowStrip(stripHost, status);

    const addBtn = (
      label: string,
      action: 'confirm' | 'unconfirm' | 'reject' | 'advance' | 'mark-ready' | 'unmark-ready',
      variant: 'primary' | 'secondary' | 'danger' = 'secondary',
    ): void => {
      const btn = document.createElement('button');
      btn.className = `lr-wf-btn lr-wf-${variant}`;
      btn.textContent = label;
      btn.addEventListener('click', async () => {
        btn.disabled = true;
        const result = await this.api.transitionTarget(composite, action);
        btn.disabled = false;
        if (result) {
          // Refresh status for this key so the buttons recompute
          await this.refreshStatusForKey(key);
        }
      });
      wrap.appendChild(btn);
    };

    // In-flight MT fill: stage transitions would 423 (TARGET_BUSY) until the
    // pipeline sweep finishes, so swap the buttons for an honest note. Editing
    // itself stays available — canEdit is the permission truth (the server
    // ignores the transient gate for status) and busy saves queue + retry.
    const fillBusy = this.api.targetBusy;
    if (fillBusy && canEdit && !isLocked && !isReady) {
      const note = document.createElement('span');
      note.className = 'lr-wf-note';
      note.textContent =
        'Machine translation is filling this page — edits save automatically; stage actions unlock in a moment.';
      wrap.appendChild(note);
    }

    // Translator-side actions: only show when the caller can actually act
    // on the row. Without canEdit gating, a translator sees Confirm on a
    // row they've already pushed past their stage and gets a 403.
    //
    // Two-step flow (Confirm → Advance) mirrors the dashboard:
    //   - Confirm: marks is_confirmed=True at current stage (does NOT move stage)
    //   - Advance: bumps stage_index, clears is_confirmed for the next reviewer
    // This keeps Confirm and Advance legible as separate intents on both surfaces.
    if (!fillBusy && canEdit && !isLocked && !isReady) {
      if (!isConfirmed) {
        addBtn('Confirm', 'confirm', 'primary');
        // A stage-0 row has nowhere to be sent back to — the server refuses
        // the reject ("already at the first stage"), so don't offer it.
        // Unknown stageIndex (older backend) stays permissive: server decides.
        if (status?.stageIndex !== 0) {
          addBtn('Reject', 'reject', 'danger');
        }
      } else {
        // Confirmed at current stage — show Advance (per_row only) and Unconfirm
        // as the next-step options. Hide Advance for per_file workflows (rows
        // advance together as a target from the dashboard) and for rows at/past
        // the workflow's postreview boundary — the server rejects that advance
        // ("already past the final stage"), so don't offer a button that can
        // only 400.
        const isPerFile = this.workflowInfo?.workflowMode === 'per_file';
        const atPostreview = isRowAtPostreview(this.workflowInfo, status?.stageIndex);
        if (!isPerFile && !atPostreview) {
          addBtn('Advance', 'advance', 'primary');
        }
        addBtn('Unconfirm', 'unconfirm');
        if (!isPerFile && atPostreview) {
          const note = document.createElement('span');
          note.className = 'lr-wf-note';
          // A project with no workflow at all (continuous projects may defer
          // choosing one) reads differently from a row parked at final review.
          note.textContent =
            this.workflowInfo && this.workflowInfo.stages.length === 0
              ? 'No translation workflow configured — set one in project settings to use stages.'
              : 'In final review — no further stages to advance.';
          wrap.appendChild(note);
        }
      }
    }

    // PM-only actions — PM/admin can always Mark/Unmark, no canEdit gate
    // (PM/admin is_pm_admin override in can_user_edit_row keeps canEdit
    // True for them anyway).
    if (isPM) {
      if (!isReady) {
        addBtn('Mark Ready', 'mark-ready', 'primary');
      } else {
        addBtn('Unmark Ready', 'unmark-ready', 'danger');
      }
    }

    // Helpful breadcrumb for translators when they see a row they can't
    // edit — tells them why no action buttons appeared.
    if (!canEdit && !isPM && !isReady && !isLocked) {
      const note = document.createElement('span');
      note.className = 'lr-wf-note';
      note.textContent =
        typeof status?.stageIndex === 'number' && status.stageIndex > 0
          ? `Row already advanced (stage ${status.stageIndex + 1}) — out of your scope.`
          : 'You are not assigned to this row at the current stage.';
      wrap.appendChild(note);
    }

    if (wrap.children.length > 0) {
      this.bodyEl.appendChild(wrap);
    }
  }

  /**
   * Render the workflow context strip ('Workflow: A → B → C — you are at stage X').
   * Highlights the user's POV stage and greys the rest.
   *
   * Highlight rule:
   *   - PM/admin: highlight = row's current stage_index (deep-dive view)
   *   - Non-PM:   highlight = first userAssignedStages entry (fixed POV)
   *
   * Renders immediately from the latest known shape, then revalidates through
   * the API's TTL-bound per-locale cache (cache hits resolve instantly) so a
   * mid-session workflow change or a per-target override is picked up. While
   * the first fetch is in flight, the host stays empty (no flash).
   */
  private renderWorkflowStrip(host: HTMLElement, status: TargetStatus | null | undefined): void {
    if (this.workflowInfo) {
      const node = this.buildWorkflowStripNode(this.workflowInfo, status);
      host.replaceChildren(...(node ? [node] : []));
    }
    void this.api.fetchWorkflowInfo(this.locale).then((info) => {
      // Same object ⇒ cache hit, nothing changed. null keeps the last known
      // shape rather than blanking the strip on a transient fetch failure.
      if (!info || info === this.workflowInfo) return;
      this.workflowInfo = info;
      // The user may have clicked another row by now. Only render if still
      // on the same key — re-resolve status from the cache.
      if (!this.current) return;
      const fresh = this.statusMap.get(this.current.key) ?? status ?? null;
      const node = this.buildWorkflowStripNode(info, fresh);
      if (host.isConnected) host.replaceChildren(...(node ? [node] : []));
    });
  }

  private buildWorkflowStripNode(
    info: EditorWorkflowInfo,
    status: TargetStatus | null | undefined,
  ): HTMLElement | null {
    if (!info.stages || info.stages.length === 0) return null;

    let highlightIndex: number | null = null;
    if (info.isPmAdmin) {
      highlightIndex = typeof status?.stageIndex === 'number' ? status.stageIndex : null;
    } else if (info.userAssignedStages.length > 0) {
      highlightIndex = info.userAssignedStages[0];
    }

    const strip = document.createElement('div');
    strip.className = 'lr-wf-strip';

    const stagesRow = document.createElement('div');
    stagesRow.className = 'lr-wf-strip-stages';
    info.stages.forEach((stage, i) => {
      const item = document.createElement('span');
      item.className = 'lr-wf-strip-stage';
      if (highlightIndex !== null && stage.index === highlightIndex) {
        item.classList.add('is-active');
      } else {
        item.classList.add('is-muted');
      }
      item.textContent = stage.name;
      stagesRow.appendChild(item);
      if (i < info.stages.length - 1) {
        const sep = document.createElement('span');
        sep.className = 'lr-wf-strip-sep';
        sep.textContent = '→';
        stagesRow.appendChild(sep);
      }
    });
    strip.appendChild(stagesRow);

    // Caption: 'You are here — stage N (Name)' or row position for PM
    const caption = document.createElement('div');
    caption.className = 'lr-wf-strip-caption';
    if (highlightIndex !== null) {
      const stage = info.stages.find((s) => s.index === highlightIndex);
      const name = stage?.name ?? `Stage ${highlightIndex + 1}`;
      const label = info.isPmAdmin ? 'Row currently at' : 'You are here';
      caption.textContent = `${label}: stage ${highlightIndex + 1} of ${info.stages.length} — ${name}`;
    } else if (!info.isPmAdmin && info.userAssignedStages.length === 0) {
      caption.textContent = 'You have no stage assignment on this project.';
    } else {
      caption.textContent = `${info.stages.length} stages`;
    }
    strip.appendChild(caption);

    return strip;
  }

  /** Re-fetch status for one key and re-render the current view. */
  private async refreshStatusForKey(key: string): Promise<void> {
    const fresh = await this.api.fetchStatus([key], this.locale, this.namespace);
    for (const [k, s] of Object.entries(fresh)) {
      this.statusMap.set(k, s);
    }
    if (this.current) {
      // Re-render the current key to pick up new status
      this.renderContent(this.current);
    }
    this.emitProgress();
  }

  /**
   * Render an "Open linked page →" button when the selected block is or
   * contains a same-origin link. Cross-origin links are skipped — the editor
   * session only applies to this site. No-op when there's no usable link.
   */
  private renderLinkNav(element: Element): void {
    const anchor = (element.matches?.('a[href]')
      ? element
      : element.querySelector?.('a[href]')) as HTMLAnchorElement | null;
    if (!anchor) return;

    let dest: URL;
    try {
      dest = new URL(anchor.href, window.location.href);
    } catch {
      return;
    }
    if (dest.origin !== window.location.origin) return;
    // Skip a link that just points at the current page (e.g. "#" anchors).
    if (dest.pathname === window.location.pathname && !dest.hash) return;

    this.addField('Linked page', () => {
      const btn = document.createElement('button');
      btn.className = 'lr-link-nav';
      btn.textContent = 'Open linked page →';
      btn.title = dest.pathname + dest.search;
      btn.addEventListener('click', () => {
        // Localize so we stay in the operator's current locale (e.g. /es).
        window.location.href = localizeUrl(dest.href, this.defaultLocale);
      });
      return btn;
    });
  }

  private addField(label: string, contentFn: () => HTMLElement): void {
    const field = document.createElement('div');
    field.className = 'lr-field';
    const labelEl = document.createElement('span');
    labelEl.className = 'lr-field-label';
    labelEl.textContent = label;
    field.appendChild(labelEl);
    field.appendChild(contentFn());
    this.bodyEl.appendChild(field);
  }

  /**
   * Get CSS class for the status dot based on actual row state, with one
   * viewer-relative override: rows that have advanced past the viewer's
   * stage assignment render as 'readonly' (greyed) — same data for everyone,
   * different signal per viewer. Avoids the new-color-per-stage explosion the
   * user was originally considering; reuses existing semantics.
   */
  private getDotClass(key: string): string {
    if (this.unsavedKeys.has(key)) return 'unsaved';
    const s = this.statusMap.get(key);
    // Missing row: grey it out when this session can't create rows
    // (creation is stage-0 work — mirrors the batch save's create gate).
    if (!s) return this.api.canCreateMissing ? 'missing' : 'readonly';
    if (s.isLocked) return 'locked';
    // Viewer-relative read-only: row is past my stage and I can't act on it.
    // Sits above ready/confirmed/edited so the dot signals 'hands off' even
    // if the row's underlying state would otherwise be green/orange.
    if (s.canEdit === false && typeof s.stageIndex === 'number' && s.stageIndex > 0) return 'readonly';
    // Green means WOULD ACTUALLY BE SERVED, not merely approved. isReady is
    // approval only: an approved row with an EMPTY target is approved and
    // delivers nothing, and used to show green here. willDeliver is the
    // server's delivery answer (approved-or-locked AND non-empty target AND
    // translatable source AND live target).
    // Fallback is isReady, NOT false: a backend predating willDeliver omits
    // the key, and `?? false` would turn every dot non-ready against such a
    // server. `?? isReady` keeps today's behaviour when absent and is correct
    // when present. Only the DOT moves — the Mark/Unmark toggle below still
    // reads isReady, because it is the only control that un-approves a row.
    if (s.willDeliver ?? s.isReady) return 'ready';
    if (s.isConfirmed) return 'confirmed';
    if (s.rowState === 'edited') return 'edited';
    if (s.rowState === 'machine_translated') return 'mt';
    return 'missing';
  }

  private renderTextWithTags(text: string): string {
    // Highlight indexed tags like <1>, </1>. The surrounding prose is
    // page-controlled (any visitor-visible text, e.g. a hostile comment),
    // and this string goes to innerHTML in the editor realm — which holds
    // the PM's session JWT — so everything is escaped; only the marker
    // wrapper spans are real markup.
    const tagPattern = /<(\/?)?(\d+)(\/)?>/g;
    let result = '';
    let lastIndex = 0;
    let match;
    while ((match = tagPattern.exec(text)) !== null) {
      result += escapeHtml(text.slice(lastIndex, match.index));
      result += `<span class="lr-field-tag">${escapeHtml(match[0])}</span>`;
      lastIndex = match.index + match[0].length;
    }
    return result + escapeHtml(text.slice(lastIndex));
  }

  private renderPreview(text: string, styles: Record<string, string[]>): string {
    if (!text) return '';
    if (Object.keys(styles).length > 0) {
      const unit: ContentUnit = { text, styles };
      // Serializer output is sanitized (core Phase 2).
      return htmlSerializer.serialize(unit);
    }
    // Plain-text branch: textarea content is user/page-controlled prose.
    return escapeHtml(text);
  }

  private initResize(handle: HTMLDivElement): void {
    let startX = 0;
    let startWidth = 0;

    const onMouseMove = (e: MouseEvent) => {
      const newWidth = startWidth + (startX - e.clientX);
      const clamped = Math.max(280, Math.min(600, newWidth));
      this.el.style.width = `${clamped}px`;
    };

    const onMouseUp = () => {
      handle.classList.remove('active');
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    handle.addEventListener('mousedown', (e) => {
      e.preventDefault();
      startX = e.clientX;
      startWidth = this.el.offsetWidth;
      handle.classList.add('active');
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    });
  }

  private refreshBlocksList(): void {
    const existing = this.bodyEl.querySelector('.lr-blocks-section');
    if (existing) existing.remove();
    this.renderBlocksList();
  }

  private renderBlocksList(): void {
    const section = document.createElement('div');
    section.className = 'lr-blocks-section';

    const blocks = this.blockExtractor.extract(document.body, true);
    const translatedCount = this.getTranslatedCount();

    const title = document.createElement('div');
    title.className = 'lr-blocks-title';
    title.textContent = `Blocks on this page (${translatedCount}/${blocks.length} translated)`;
    section.appendChild(title);

    for (const block of blocks) {
      const item = document.createElement('div');
      item.className = 'lr-block-item';

      const isActive = this.current && block.element === this.current.element;
      if (isActive) item.classList.add('active');

      // Status dot
      const dot = document.createElement('span');
      dot.className = 'lr-status-dot';
      dot.classList.add(this.getDotClass(block.key));
      item.appendChild(dot);

      // Source text
      const textWrap = document.createElement('div');
      textWrap.className = 'lr-block-text';
      const displayText = block.unit.text.replace(/<\/?[0-9]+\/?>/g, '');
      const sourceSpan = document.createElement('div');
      sourceSpan.className = 'lr-block-source';
      sourceSpan.textContent = displayText.length > 60 ? displayText.slice(0, 60) + '...' : displayText;
      sourceSpan.title = displayText;
      textWrap.appendChild(sourceSpan);

      // Translation preview (if exists)
      const blockStatus = this.statusMap.get(block.key);
      if (blockStatus && blockStatus.text) {
        const previewSpan = document.createElement('div');
        previewSpan.className = 'lr-block-preview';
        const previewText = blockStatus.text.replace(/<\/?[0-9]+\/?>/g, '');
        previewSpan.textContent = previewText.length > 50 ? previewText.slice(0, 50) + '...' : previewText;
        textWrap.appendChild(previewSpan);
      }

      item.appendChild(textWrap);

      item.addEventListener('click', () => {
        block.element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        this.onBlockClick({ key: block.key, unit: block.unit, translatedUnit: null, element: block.element });
      });

      section.appendChild(item);
    }

    this.bodyEl.appendChild(section);
  }
}
